import { lazy, Suspense, useEffect } from 'react'
import { Routes, Route, Navigate, useParams, Outlet } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  supportedLanguages,
  defaultLanguage,
  type SupportedLanguage,
} from '@/i18n'
import { Seo } from '@/components/Seo'
import { Header } from '@/components/layout'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { RequirePermission } from '@/components/admin/permissions'
import { localizedRoutes } from '@/i18n/routes'
import { Login } from '@/pages/Login'
import { Home } from '@/pages/Home'
import { TournamentsPage } from '@/pages/zambepong/TournamentsPage'
import { TournamentPage } from '@/pages/zambepong/TournamentPage'
import { MatchPage } from '@/pages/zambepong/MatchPage'
import { ZambeBetPage } from '@/pages/zambepong/ZambeBetPage'
import { ZambeBetMatchPage } from '@/pages/zambepong/ZambeBetMatchPage'

// Area riservata in un chunk a parte (un solo `import()`, vedi pages/admin/lazy.ts): il sito pubblico non la scarica.
const loadAdmin = () => import('@/pages/admin/lazy')
const lazyAdmin = (name: keyof Awaited<ReturnType<typeof loadAdmin>>) => lazy(() => loadAdmin().then((m) => ({ default: m[name] })))
const AdminLayout = lazyAdmin('AdminLayout')
const AdminHome = lazyAdmin('AdminHome')
const GroupEdit = lazyAdmin('GroupEdit')
const GroupsList = lazyAdmin('GroupsList')
const RequestEdit = lazyAdmin('RequestEdit')
const RequestsList = lazyAdmin('RequestsList')
const UserEdit = lazyAdmin('UserEdit')
const UsersList = lazyAdmin('UsersList')
const MatchDashboard = lazyAdmin('MatchDashboard')
const TournamentEdit = lazyAdmin('TournamentEdit')
const TournamentsList = lazyAdmin('TournamentsList')

function LanguageWrapper() {
  const { lang } = useParams<{ lang: string }>()
  const { i18n } = useTranslation()

  useEffect(() => {
    if (lang && supportedLanguages.includes(lang as SupportedLanguage)) {
      if (i18n.language !== lang) {
        i18n.changeLanguage(lang)
      }
      // Lettori di schermo e sillabazione seguono la lingua dell'URL, non quella di index.html.
      document.documentElement.lang = lang
    }
  }, [lang, i18n])

  // Redirect invalid language to default
  if (!lang || !supportedLanguages.includes(lang as SupportedLanguage)) {
    return <Navigate to={`/${defaultLanguage}`} replace />
  }

  return <Outlet />
}

/** Sito pubblico: header e contenuto, senza footer (lingua e area riservata stanno nell'header); l'area gestionale ha il suo guscio (AdminLayout). */
function SiteLayout() {
  return (
    <div className="paper-grain bg-paper text-ink">
      <Seo />
      <Header />
      <Outlet />
    </div>
  )
}

export function LanguageRouter() {
  return (
    <Routes>
      {/* Redirect root to default language */}
      <Route path="/" element={<Navigate to={`/${defaultLanguage}`} replace />} />

      {/* Language-prefixed routes */}
      <Route path="/:lang" element={<LanguageWrapper />}>
        <Route element={<SiteLayout />}>
          <Route index element={<Home />} />
          <Route path={localizedRoutes.zambePong.it} element={<TournamentsPage />} />
          <Route path="zambepong/:id" element={<TournamentPage />} />
          <Route path={localizedRoutes.zambePongMatch.it} element={<MatchPage />} />
          <Route path={localizedRoutes.zambePongMatch.en} element={<MatchPage />} />
          <Route path={localizedRoutes.zambePongZambeBet.it} element={<ZambeBetPage />} />
          <Route path={localizedRoutes.zambePongZambeBetMatch.it} element={<ZambeBetMatchPage />} />
          <Route path={localizedRoutes.zambePongZambeBetMatch.en} element={<ZambeBetMatchPage />} />
          <Route path={localizedRoutes.login.it} element={<Login />} />
        </Route>

        {/* Area riservata: una voce del menu (pages/admin/navigation.ts) = lista, nuovo, modifica, ognuna col suo permesso. */}
        <Route
          path={localizedRoutes.admin.it}
          element={
            <ProtectedRoute>
              <Suspense fallback={null}>
                <AdminLayout />
              </Suspense>
            </ProtectedRoute>
          }
        >
          <Route index element={<AdminHome />} />
          <Route path="users" element={<RequirePermission permission="users.view"><UsersList /></RequirePermission>} />
          <Route path="users/new" element={<RequirePermission permission="users.manage"><UserEdit /></RequirePermission>} />
          <Route path="users/:id" element={<RequirePermission permission="users.view"><UserEdit /></RequirePermission>} />
          <Route path="groups" element={<RequirePermission permission="groups.view"><GroupsList /></RequirePermission>} />
          <Route path="groups/new" element={<RequirePermission permission="groups.manage"><GroupEdit /></RequirePermission>} />
          <Route path="groups/:id" element={<RequirePermission permission="groups.view"><GroupEdit /></RequirePermission>} />
          <Route path="requests" element={<RequirePermission permission="requests.view"><RequestsList /></RequirePermission>} />
          <Route path="requests/new" element={<RequirePermission permission="requests.manage"><RequestEdit /></RequirePermission>} />
          <Route path="requests/:id" element={<RequirePermission permission="requests.view"><RequestEdit /></RequirePermission>} />
          <Route path="zambepong" element={<RequirePermission permission="tournaments.view"><TournamentsList /></RequirePermission>} />
          <Route path="zambepong/new" element={<RequirePermission permission="tournaments.manage"><TournamentEdit /></RequirePermission>} />
          <Route path="zambepong/:id" element={<RequirePermission permission="tournaments.view"><TournamentEdit /></RequirePermission>} />
          <Route path="zambepong/:id/partite/:matchId" element={<RequirePermission permission="matches.manage"><MatchDashboard /></RequirePermission>} />
        </Route>
      </Route>
    </Routes>
  )
}
